import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { supabase } from '../lib/supabase.ts'
import {
  clearStoredPostLoginRedirect,
  getStoredPostLoginRedirect,
  resolvePortalRouteForUser,
  sanitizeRedirectTarget,
  storePostLoginRedirect,
} from '../utils/authNavigation'
import Dashboard from '../views/Dashboard.vue'
import Offertes from '../views/Offertes.vue'
import Facturen from '../views/Facturen.vue'
import Login from '../views/Login.vue'
import OfferteDetails from '../views/OfferteDetails.vue'
import FactuurDetails from '../views/FactuurDetails.vue'
import BundelDetails from '../views/BundelDetails.vue'
import RapportenDetails from '../views/RapportenDetails.vue'
import RapportHerstel from '../views/RapportHerstel.vue'
import Rapporten from '../views/Rapporten.vue'
import Documenten from '../views/Documenten.vue'
import ChangePassword from '../views/ChangePassword.vue'
import RequestPasswordReset from '../views/RequestPasswordReset.vue'
import ResetPassword from '../views/ResetPassword.vue'
import Options from '../views/Options.vue'
import Profile from '../views/Profile.vue'
import AuthCallback from '../views/AuthCallback.vue'
import AuthLinkRecovery from '../views/AuthLinkRecovery.vue'
import CompleteProfile from '../views/CompleteProfile.vue'
import WrongAppView from '../views/WrongAppView.vue'
import BetalingVoltooid from '../views/BetalingVoltooid.vue'
import LocatieAccessRequests from '../views/LocatieAccessRequests.vue'
import RapportInspectieOngoing from '../views/RapportInspectieOngoing.vue'
import RapportNaBetaling from '../views/RapportNaBetaling.vue'
import { getInspectieEntry, getInspectieTypeParam, getPreferredInspectieType, isInspectieAfgerond } from '../utils/rapportScope'
import { ensureProfileMeta } from '../utils/profileMeta'
import { captureError } from '../lib/sentry'

// A rapport that is not (yet) released (rapporten.zichtbaar = false) redirects
// to the explanatory page — with payment details when a "rapport na betaling"
// factuur is the reason. RLS enforces the actual block; this guard only
// provides the page, so an RPC failure falls through to the rapport route.
async function guardPaymentHold(rapportId: string, inspectieType: string | null) {
  try {
    const { data } = await supabase.rpc('get_rapport_payment_holds', { p_rapport_ids: [rapportId] })
    const hold = Array.isArray(data) ? (data[0] as { zichtbaar?: boolean } | undefined) : undefined
    if (hold && hold.zichtbaar === false) {
      return { name: 'RapportNaBetaling', query: { id: rapportId, ...(inspectieType ? { type: inspectieType } : {}) } }
    }
  } catch (error) {
    captureError(error)
  }
  return null
}

async function guardInspectieAccess(to: { params: Record<string, unknown> }) {
  const rapportId = String(to.params.id || '')
  const inspectieType = getInspectieTypeParam(to.params.type)

  if (!rapportId || !inspectieType) {
    return '/rapporten'
  }

  const { data } = await supabase
    .from('rapporten')
    .select('inspectieStatus')
    .eq('id', rapportId)
    .maybeSingle()

  const inspectieStatus = (data as { inspectieStatus?: any[] | null } | null)?.inspectieStatus
  const entry = getInspectieEntry(inspectieStatus as any, inspectieType)

  if (!entry) {
    const fallbackType = getPreferredInspectieType(inspectieStatus as any)
    return fallbackType ? { name: 'RapportenDetails', params: { id: rapportId, type: fallbackType } } : '/rapporten'
  }

  if (!isInspectieAfgerond(entry)) {
    return { name: 'RapportInspectieOngoing', query: { id: rapportId, type: inspectieType } }
  }

  const holdRedirect = await guardPaymentHold(rapportId, inspectieType)
  if (holdRedirect) return holdRedirect

  return true
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true, depth: 0 }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: Profile,
    meta: { requiresAuth: true, depth: 1 }
  },
  {
    path: '/complete-profile',
    name: 'CompleteProfile',
    component: CompleteProfile,
    meta: { requiresAuth: true, skipProfileCheck: true, hideNavigation: true, depth: 1 }
  },
  {
    path: '/offertes',
    name: 'Offertes',
    component: Offertes,
    meta: { requiresAuth: true, depth: 1 }
  },
  {
    path: '/facturen',
    name: 'Facturen',
    component: Facturen,
    meta: { requiresAuth: true, depth: 1 }
  },
  {
    path: '/offerte/:id',
    name: 'OfferteDetails',
    component: OfferteDetails,
    meta: { requiresAuth: true, depth: 2 }
  },
  {
    path: '/factuur/:id',
    name: 'FactuurDetails',
    component: FactuurDetails,
    meta: { requiresAuth: true, depth: 2 }
  },
  {
    path: '/bundel/:id',
    name: 'BundelDetails',
    component: BundelDetails,
    meta: { requiresAuth: true, depth: 2 }
  },
  {
    path: '/rapporten',
    name: 'Rapporten',
    component: Rapporten,
    meta: { requiresAuth: true, depth: 1 }
  },
  {
    path: '/documenten',
    name: 'Documenten',
    component: Documenten,
    meta: { requiresAuth: true, depth: 1 }
  },
  {
    path: '/rapport/:id',
    name: 'RapportenDetailsLegacy',
    component: RapportenDetails,
    beforeEnter: async (to) => {
      const rapportId = String(to.params.id || '')
      if (!rapportId) return '/rapporten'

      const { data } = await supabase
        .from('rapporten')
        .select('inspectieStatus')
        .eq('id', rapportId)
        .maybeSingle()

      if (!data) return '/rapporten'

      const inspectieType = getPreferredInspectieType((data as { inspectieStatus?: Array<{ type: any; status: any }> | null }).inspectieStatus)
      // Rapport exists but has no inspectie entries yet: land on the "inspectie
      // bezig" page so pre-inspection actions (documentvereisten) stay reachable.
      if (!inspectieType) return { name: 'RapportInspectieOngoing', query: { id: rapportId } }

      return { name: 'RapportenDetails', params: { id: rapportId, type: inspectieType } }
    },
    meta: { requiresAuth: true, depth: 2 }
  },
  {
    path: '/rapport/:id/:type',
    name: 'RapportenDetails',
    component: RapportenDetails,
    beforeEnter: async (to) => {
      if (!getInspectieTypeParam(to.params.type)) {
        return { path: `/rapport/${String(to.params.id || '')}` }
      }
      return guardInspectieAccess(to as any)
    },
    meta: { requiresAuth: true, depth: 2 }
  },
  {
    path: '/rapport/:id/herstel',
    name: 'RapportHerstelLegacy',
    component: RapportHerstel,
    beforeEnter: async (to) => {
      const rapportId = String(to.params.id || '')
      if (!rapportId) return '/rapporten'

      const { data } = await supabase
        .from('rapporten')
        .select('inspectieStatus')
        .eq('id', rapportId)
        .maybeSingle()

      const inspectieType = getPreferredInspectieType((data as { inspectieStatus?: Array<{ type: any; status: any }> | null } | null)?.inspectieStatus)
      if (!inspectieType) return '/rapporten'

      return { name: 'RapportHerstel', params: { id: rapportId, type: inspectieType } }
    },
    meta: { requiresAuth: true, depth: 3 }
  },
  {
    path: '/rapport/:id/:type/herstel',
    name: 'RapportHerstel',
    component: RapportHerstel,
    beforeEnter: async (to) => {
      if (!getInspectieTypeParam(to.params.type)) {
        return { path: `/rapport/${String(to.params.id || '')}/herstel` }
      }
      return guardInspectieAccess(to as any)
    },
    meta: { requiresAuth: true, depth: 3 }
  },
  {
    path: '/rapport-inspectie-bezig',
    name: 'RapportInspectieOngoing',
    component: RapportInspectieOngoing,
    meta: { requiresAuth: true, depth: 2 }
  },
  {
    path: '/rapport-na-betaling',
    name: 'RapportNaBetaling',
    component: RapportNaBetaling,
    meta: { requiresAuth: true, depth: 2 }
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: ChangePassword,
    meta: { requiresAuth: true, depth: 1 }
  },
  {
    path: '/request-reset',
    name: 'RequestPasswordReset',
    component: RequestPasswordReset,
    meta: { depth: 1 }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { depth: 1 }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { depth: 1 }
  },
  {
    path: '/options',
    name: 'Options',
    component: Options,
    meta: { requiresAuth: true, depth: 1 }
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: AuthCallback,
    meta: { depth: 1 }
  },
  {
    path: '/auth/link-recovery',
    name: 'AuthLinkRecovery',
    component: AuthLinkRecovery,
    meta: { depth: 1 }
  },
  {
    path: '/wrong-app',
    name: 'WrongApp',
    component: WrongAppView,
    meta: { requiresAuth: true, skipUserTypeCheck: true, hideNavigation: true, depth: 1 }
  },
  {
    path: '/locatie-access-requests',
    name: 'LocatieAccessRequests',
    component: LocatieAccessRequests,
    meta: { requiresAuth: true, depth: 1 }
  },
  {
    // Publiek: hier landt de betaler na een betaling bij de betaalprovider.
    // Geen requiresAuth — de betaler hoeft geen portaalaccount te hebben, en de
    // pagina toont bewust geen factuurgegevens.
    path: '/betaling-voltooid',
    name: 'BetalingVoltooid',
    component: BetalingVoltooid,
    meta: { depth: 1, hideNavigation: true }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFoundRedirect',
    redirect: '/',
    meta: { depth: 0 }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return new Promise((resolve) => {
        setTimeout(() => resolve({ top: 0 }), 250)
      })
    }
  },
})

router.beforeEach(async (to, _from) => {
  console.log('[Router] Navigation from', _from.path, 'to', to.path)
  const { data: { session } } = await supabase.auth.getSession()
  console.log('[Router] Session exists:', !!session, 'User:', session?.user?.email)
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const skipUserTypeCheck = to.matched.some(record => record.meta.skipUserTypeCheck)
  console.log('[Router] Route requires auth:', requiresAuth, 'Skip user type check:', skipUserTypeCheck)

  if (requiresAuth && !session) {
    console.log('[Router] No session, redirecting to login')
    storePostLoginRedirect(to.fullPath)
    return { path: '/login', query: { redirect: to.fullPath } }
  }

  if (to.path === '/login' && session) {
    const redirectTarget = sanitizeRedirectTarget(
      typeof to.query.redirect === 'string' ? to.query.redirect : getStoredPostLoginRedirect() || '/',
    )

    try {
      const destination = await resolvePortalRouteForUser(session.user.id, redirectTarget)
      console.log('[Router] Already logged in, redirecting to:', destination)
      clearStoredPostLoginRedirect()
      return destination
    } catch (error) {
      console.warn("Error checking user type:", error)
      console.log('[Router] Error, redirecting to stored target anyway:', redirectTarget)
      clearStoredPostLoginRedirect()
      return redirectTarget
    }
  }

  if (to.path === '/auth/link-recovery' && session) {
    const redirectTarget = sanitizeRedirectTarget(
      typeof to.query.redirect === 'string' ? to.query.redirect : getStoredPostLoginRedirect() || '/',
    )

    try {
      const destination = await resolvePortalRouteForUser(session.user.id, redirectTarget)
      clearStoredPostLoginRedirect()
      return destination
    } catch (error) {
      console.warn('Error resolving auth link recovery redirect:', error)
      clearStoredPostLoginRedirect()
      return redirectTarget
    }
  }

  if (session && to.path === '/wrong-app' && !to.query.types) {
    try {
      const destination = await resolvePortalRouteForUser(session.user.id, '/')

      if (typeof destination === 'string') {
        console.log('[Router] Allowed user should not stay on wrong-app, redirecting home')
        return destination
      } else {
        console.log('[Router] Restoring missing wrong-app state from user type check')
        return destination
      }
    } catch (error) {
      console.warn('Error restoring wrong-app state:', error)
      return true
    }
  }

  // First-login completion: accounts created via rapport sharing have
  // users.profile_completed = false and must fill in their details before
  // using the portal. Cached per user, so this is one query on first hit.
  const skipProfileCheck = to.matched.some(record => record.meta.skipProfileCheck)
  if (session && requiresAuth && !skipProfileCheck && to.path !== '/wrong-app') {
    try {
      const meta = await ensureProfileMeta()
      if (meta && meta.profile_completed === false) {
        console.log('[Router] Profile incomplete, redirecting to complete-profile')
        return { path: '/complete-profile', query: { redirect: to.fullPath } }
      }
    } catch (profileError) {
      // Fail open, matching the user-type check below — a DB hiccup should
      // never lock users out of the portal.
      console.warn('Profile completion check failed, allowing through:', profileError)
    }
  }

  if (session && requiresAuth && !skipUserTypeCheck && to.path !== '/wrong-app') {
    try {
      const destination = await resolvePortalRouteForUser(session.user.id, to.fullPath)

      if (typeof destination === 'string') {
        console.log('[Router] User allowed, proceeding to route')
        return true
      } else {
        console.warn('User not allowed in this app, redirecting to wrong-app page')
        return destination
      }
    } catch (error) {
      console.warn("Error checking user type:", error)
      // Allow through on error (fail open)
      console.log('[Router] Error, allowing through anyway')
      return true
    }
  }

  console.log('[Router] No special conditions, proceeding')
  return true
})

// Report navigation failures (lazy-load errors, guard exceptions) to Sentry.
router.onError((error, to) => {
  console.error('[Router] Navigation error:', error)
  captureError(error, { route: to.fullPath })
})

export default router
